import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight, Store, Gamepad2, Plane, UtensilsCrossed, Zap, Globe2, ShieldCheck, Award,
  Search, Tag, Gift,
} from "lucide-react";
import Reveal, { SectionHeading } from "@/components/Reveal";
import BrandLogo, { matchBrandSrc } from "@/components/BrandLogo";
import CTASection from "@/components/CTASection";
import { CATALOG, CATEGORIES, CATEGORY_LABELS } from "@/data/catalog";
import { REGION_LINKS } from "@/data/regions";
import { useSEO } from "@/hooks/useSEO";

const categories = [
  {
    icon: Store,
    title: "Retail & Lifestyle",
    body: "The world's most loved shopping and lifestyle brands, delivered digitally.",
    logos: ["amazon", "apple", "ebay", "gamestop", "uber", "googleplay"],
  },
  {
    icon: Gamepad2,
    title: "Gaming",
    body: "Subscriptions, in-game currency, and gaming credit for every platform.",
    logos: ["steam", "xbox", "playstation", "nintendo", "minecraft", "roblox"],
  },
  {
    icon: Plane,
    title: "Travel & Transport",
    body: "Rides, journeys, and experiences, gifted in seconds.",
    logos: ["careem", "uber", "ola"],
    brands: ["Airlines", "Hotels", "Travel experiences"],
  },
  {
    icon: UtensilsCrossed,
    title: "Food & Dining",
    body: "Restaurant and food delivery vouchers for every taste.",
    logos: ["zomato", "swiggy"],
    brands: ["Talabat", "Game & Grub", "Restaurant vouchers", "Food delivery"],
  },
];

const highlights = [
  { icon: Award, value: "500+", label: "Global digital brands" },
  { icon: Globe2, value: "120+", label: "Countries covered" },
  { icon: Zap, value: "Seconds", label: "Average digital delivery" },
  { icon: ShieldCheck, value: "24/7", label: "Reliable fulfillment" },
];

export function CatalogCard({ item }) {
  const src = matchBrandSrc(item.name);
  return (
    <div
      className="spotlight group rounded-2xl border border-[#e8ecf4] bg-white p-5 transition-all duration-300 hover:-translate-y-0.5 hover:border-[#0816a8]/35 hover:shadow-[0_16px_32px_-16px_rgba(8,22,168,0.25)]"
      onMouseMove={(e) => {
        const r = e.currentTarget.getBoundingClientRect();
        e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
        e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
      }}
      data-testid={`catalog-item-${item.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          {src ? (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-[#e8ecf4] bg-[#f7f9fc] p-1.5">
              <img src={src} alt="" loading="lazy" className="h-full w-full object-contain" />
            </span>
          ) : (
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-[#0816a8] to-[#3b4ed8]">
              <Gift className="h-5 w-5 text-white" />
            </span>
          )}
          <h3 className="text-sm font-semibold leading-snug text-[#111827]">{item.name}</h3>
        </div>
        <span className="shrink-0 rounded-full border border-[#0816a8]/25 bg-[#0816a8]/[0.06] px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#0816a8]">
          {item.region}
        </span>
      </div>
      <p className="mt-1.5 text-xs text-[#64748b]">{CATEGORY_LABELS[item.category]}</p>
      <Link
        to="/contact"
        data-testid={`catalog-offer-${item.id}`}
        className="mt-4 flex items-center gap-1.5 text-xs font-semibold text-[#c98a00] transition-colors duration-200 hover:text-[#f4a900]"
      >
        <Tag className="h-3.5 w-3.5" />
        Ask for Best Offer
        <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
      </Link>
    </div>
  );
}

export default function Products() {
  useSEO(
    "Global Gift Card Catalog — 310+ Products | Vowpin Global",
    "Browse 310+ digital gift cards, gaming vouchers, and branded experiences from 500+ global brands across India, UAE, KSA, USA and 120+ countries. Instant delivery, volume pricing."
  );
  const [cat, setCat] = useState("all");
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATALOG.filter(
      (p) =>
        (cat === "all" || p.category === cat) &&
        (!q || p.name.toLowerCase().includes(q) || p.region.toLowerCase().includes(q))
    );
  }, [cat, query]);

  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden bg-white py-20 lg:py-28">
        <div className="relative mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#c98a00]">Products · Gift Card Catalog</p>
            <h1 className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-[1.08] tracking-tight text-[#111827] sm:text-5xl lg:text-6xl">
              Explore Our <span className="text-gradient-blue">Global Catalog</span>
            </h1>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-[#64748b] md:text-lg">
              Digital gift cards, gaming vouchers, and branded experiences from 500+ of the world's
              leading brands — delivered instantly, anywhere.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/contact"
                data-testid="products-request-catalogue-btn"
                className="group flex items-center gap-2 rounded-full bg-[#0816a8] px-8 py-4 text-sm font-semibold text-white shadow-[0_10px_28px_-8px_rgba(8,22,168,0.5)] transition-all duration-300 hover:bg-[#0a1bc4] hover:shadow-[0_14px_36px_-8px_rgba(8,22,168,0.6)]"
              >
                Request Full Catalogue
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </Link>
              <Link
                to="/contact"
                data-testid="products-talk-to-sales-btn"
                className="rounded-full border border-[#d7dce8] bg-white px-8 py-4 text-sm font-semibold text-[#374151] transition-colors duration-300 hover:border-[#0816a8]/50 hover:text-[#0816a8]"
              >
                Talk to Sales
              </Link>
            </div>
          </Reveal>

          <div className="mt-16 grid grid-cols-2 gap-5 lg:grid-cols-4">
            {highlights.map((h, i) => (
              <Reveal key={h.label} delay={i * 0.07}>
                <div className="glow-card flex items-center gap-4 rounded-[20px] p-5" data-testid={`catalog-highlight-${i}`}>
                  <h.icon className="h-6 w-6 shrink-0 text-[#0816a8]" />
                  <div>
                    <p className="font-display text-xl font-semibold text-[#111827]">{h.value}</p>
                    <p className="text-xs text-[#64748b]">{h.label}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="border-y border-[#e8ecf4] bg-[#f7f9fc] py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Categories"
            title="Every brand your recipients love"
            sub="A curated selection below — the full catalogue is available on request."
          />
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {categories.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.07}>
                <div className="glow-card h-full rounded-[24px] p-10" data-testid={`catalog-category-${i}`}>
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#0816a8]/[0.06] ring-1 ring-[#0816a8]/15">
                    <c.icon className="h-6 w-6 text-[#0816a8]" />
                  </div>
                  <h2 className="mt-6 font-display text-2xl font-semibold text-[#111827]">{c.title}</h2>
                  <p className="mt-3 text-sm leading-relaxed text-[#64748b] md:text-base">{c.body}</p>
                  {c.logos && (
                    <div className="mt-7 grid grid-cols-3 gap-3">
                      {c.logos.map((slug) => (
                        <BrandLogo key={slug} slug={slug} large />
                      ))}
                    </div>
                  )}
                  {c.brands && (
                    <div className="mt-5 flex flex-wrap gap-2.5">
                      {c.brands.map((b) => (
                        <span
                          key={b}
                          className="rounded-full border border-[#f4a900]/40 bg-[#f4a900]/[0.08] px-4 py-1.5 text-sm font-medium text-[#b87e00]"
                        >
                          {b}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Full catalog browser */}
      <section className="bg-white py-24" id="catalog">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Live Inventory"
            title="Browse the Full Catalog"
            sub="Every product below is in active distribution. Prices are tailored to volume — ask for your best offer."
          />

          <Reveal delay={0.1}>
            <div className="mt-12 flex flex-col gap-5">
              <div className="relative max-w-md">
                <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#9aa3b5]" />
                <input
                  data-testid="catalog-search-input"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search products or regions…"
                  className="w-full rounded-full border border-[#d7dce8] bg-white py-3.5 pl-11 pr-5 text-sm text-[#111827] placeholder-[#9aa3b5] outline-none transition-colors duration-200 focus:border-[#0816a8]"
                />
              </div>
              <div className="flex flex-wrap gap-2.5" data-testid="catalog-filters">
                {CATEGORIES.map((c) => (
                  <button
                    key={c.key}
                    data-testid={`filter-${c.key}`}
                    onClick={() => setCat(c.key)}
                    className={`rounded-full px-5 py-2.5 text-xs font-semibold transition-all duration-300 ${
                      cat === c.key
                        ? "bg-[#0816a8] text-white shadow-[0_8px_20px_-6px_rgba(8,22,168,0.5)]"
                        : "border border-[#e5e9f2] bg-white text-[#64748b] hover:border-[#0816a8]/40 hover:text-[#0816a8]"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap items-center gap-2.5" data-testid="catalog-region-links">
                <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9aa3b5]">Regional catalogues:</span>
                {REGION_LINKS.map((r) => (
                  <Link
                    key={r.slug}
                    to={r.to}
                    data-testid={`catalog-region-${r.slug}`}
                    className="group inline-flex items-center gap-1.5 rounded-full border border-[#f4a900]/40 bg-[#f4a900]/[0.08] px-4 py-2 text-xs font-semibold text-[#b87e00] transition-colors duration-300 hover:border-[#f4a900]"
                  >
                    {r.label}
                    <ArrowRight className="h-3 w-3 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                ))}
              </div>
              <p className="text-xs uppercase tracking-[0.2em] text-[#9aa3b5]" data-testid="catalog-count">
                {filtered.length} products
              </p>
            </div>
          </Reveal>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="catalog-grid">
            {filtered.map((item) => (
              <CatalogCard key={item.id} item={item} />
            ))}
          </div>

          {filtered.length === 0 && (
            <p className="mt-10 text-sm text-[#64748b]" data-testid="catalog-empty">
              No products match your search — try another name or region.
            </p>
          )}
        </div>
      </section>

      <CTASection
        title="Want volume pricing on any product?"
        sub="Every item in the catalog is priced to your volume and markets — ask for your best offer today."
      />
    </main>
  );
}
